import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { creatorService } from "../../services/creatorService";

interface Creator {
  id: string;
  displayName: string;
  category?: string;
  avatar?: string;
  banner?: string;
  customUrl?: string;
  _count?: {
    patrons: number;
  };
  user?: {
    avatar?: string;
  };
}

const placeholderCreators: Creator[] = [
  {
    id: "1",
    displayName: "Rachel Maksy",
    category: "Video creator",
    avatar: "https://images.unsplash.com/photo-1494790778202-cad84cf45f1d?w=400&h=500&fit=crop&q=80",
    customUrl: "rachelmaksy",
  },
  {
    id: "2",
    displayName: "Tim Chantarangsu",
    category: "Podcaster",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=500&fit=crop&q=80",
    customUrl: "timchantarangsu",
  },
  {
    id: "3",
    displayName: "Jade Novah",
    category: "Musician",
    avatar: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400&h=500&fit=crop&q=80",
    customUrl: "jadenovah",
  },
  {
    id: "4",
    displayName: "RossDraws",
    category: "Visual artist",
    avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=500&fit=crop&q=80",
    customUrl: "rossdraws",
  },
  {
    id: "5",
    displayName: "KAMAUU",
    category: "Musician",
    avatar: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&h=500&fit=crop&q=80",
    customUrl: "kamauu",
  },
  {
    id: "6",
    displayName: "Tina Yu",
    category: "Visual artist",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=500&fit=crop&q=80",
    customUrl: "tinayuart",
  },
  {
    id: "7",
    displayName: "Kevin Woo",
    category: "Content creator",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=500&fit=crop&q=80",
    customUrl: "kevinwoo",
  },
  {
    id: "8",
    displayName: "Ellis Ever After",
    category: "Podcaster",
    avatar: "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=400&h=500&fit=crop&q=80",
    customUrl: "elliseverafter",
  },
];

const CreatorShowcase = () => {
  const [creators, setCreators] = useState<Creator[]>(placeholderCreators);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCreators = async () => {
      try {
        const response = await creatorService.searchCreators({
          query: "",
          limit: 8,
          sort: "popular",
        });
        if (response.creators && response.creators.length > 0) {
          setCreators(response.creators);
        }
      } catch (error) {
        console.error("Failed to fetch creators:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCreators();
  }, []);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const getCreatorImage = (creator: Creator) => {
    // Prefer banner, then avatar fields, then fallback
    return (
      creator.banner ||
      creator.avatar ||
      creator.user?.avatar ||
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=500&fit=crop&q=80"
    );
  };

  const getCreatorUrl = (creator: Creator) => {
    return `/creator/${creator.customUrl || creator.id}`;
  };

  if (loading) {
    return (
      <section id="creators" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-bold mb-6" style={{ fontFamily: "var(--patreon-font-secondary)" }}>
              Meet our creators
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-gray-200 rounded-2xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="creators" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2
            className="text-5xl lg:text-6xl font-bold mb-6"
            style={{
              fontFamily: "var(--patreon-font-secondary)",
              letterSpacing: "-0.02em",
            }}
          >
            Meet our creators
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Join thousands of creators building sustainable businesses with their most passionate fans
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
        >
          {creators.map((creator) => (
            <motion.div key={creator.id} variants={itemVariants}>
              <Link
                to={getCreatorUrl(creator)}
                className="group block relative overflow-hidden rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={getCreatorImage(creator)}
                    alt={creator.displayName}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                <div className="p-5 space-y-2">
                  <p className="text-sm font-medium text-gray-500">{creator.category || "Creator"}</p>
                  <h3 className="text-xl font-semibold text-gray-900">{creator.displayName}</h3>
                  {creator._count?.patrons && creator._count.patrons > 0 && (
                    <p className="text-sm text-gray-600">
                      {creator._count.patrons} patron{creator._count.patrons !== 1 ? "s" : ""}
                    </p>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mt-12"
        >
          <Link
            to="/discover"
            className="inline-flex items-center text-lg font-semibold hover:underline"
            style={{ color: "var(--patreon-btn-primary-bg)" }}
          >
            Discover more creators
            <svg
              className="ml-2 w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default CreatorShowcase;
